import { MoreOutlined } from '@ant-design/icons';
import {
  PRODUCT_COLUMN_VISIBILITY_CONFIG,
  PRODUCT_DRAG_COLUMN_STORAGE_KEY,
  PRODUCT_FIXED_COLUMN_KEYS,
  ProductTable,
} from '@features/products/components/productExhibition/ProductTable';
import { productStatuses } from '@features/products/constants/productStatuses.const';
import { ProductDto } from '@features/products/interfaces/productInterface';
import { ToastMessage } from '@molecules';
import { useQuery } from '@tanstack/react-query';
import { PageLimitFilter } from '@typings';
import { Button, Dropdown, Row, Tag } from 'antd';
import { MenuProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import type { PageListProps } from 'src/app/framework/templates/PageList/PageList.interface';
import { usePageListColumnOrder } from 'src/app/framework/templates/PageList/usePageListColumnOrder';
import { usePageListColumnVisibility } from 'src/app/framework/templates/PageList/usePageListColumnVisibility';
import { firstLetterUpperCase } from 'src/app/helpers/StringHelper';
import { usePaginationFilter } from 'src/app/hooks/usePaginationFilter';
import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';
import { buildProductCheckoutLink } from 'src/features/offers/utils/helpers';

import { productApi } from '../api/productApi';
import { TabsProductsProps } from '../components/productExhibition/tabs/TabsProducts';
import { useOfferBulkImport } from './useOfferBulkImport';
import { useProfileProduct } from './useProfileProduct';
import { useProviderProduct } from './useProviderProduct';

export type FilterProducts = { search?: string; status?: string };

const PRODUCT_PAGE_SIZE = 20;

const colorByStatus = {
  active: 'green',
  inactive: 'magenta',
};

export default function useProductExhibition() {
  const [switchActiveProduct, setSwitchActiveProduct] =
    useState<ProductDto | null>(null);
  const [isLoadingSwitchStatus, setIsLoadingSwitchStatus] = useState(false);

  const { ActionsOfferBulk, DrawerOfferBulk } = useOfferBulkImport();

  useBreadcrumbs([
    {
      label: 'Produtos',
    },
  ]);

  const navigate = useNavigate();

  const {
    currentFilter,
    handleChangeFilter,
    handleChangeOrder,
    handleChangePagination,
    tableColumnSortOrderSymbol,
  } = usePaginationFilter<PageLimitFilter>(PRODUCT_PAGE_SIZE);

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ['products', currentFilter],
    queryFn: async () => {
      const data = await productApi.getProductsByPage(currentFilter);

      return data;
    },
  });

  useEffect(() => {
    if (productsError) {
      console.error('Erro ao carregar produtos:', productsError);

      ToastMessage({
        type: 'error',
        content:
          'Ocorreu um erro ao carregar os produtos. Por favor, tente novamente.',
      });
    }
  }, [productsError]);

  const { profilesData, isLoadingProfiles } = useProfileProduct();
  const { providersData, isLoadingProviders } = useProviderProduct();

  const handleSwitchActiveProduct = async () => {
    try {
      if (!switchActiveProduct) return;

      setIsLoadingSwitchStatus(true);

      await productApi.toggleStatusProduct(
        switchActiveProduct.id,
        !switchActiveProduct.active
      );
      await refetchProducts();
      ToastMessage({
        type: 'success',
        content: `Produto ${!switchActiveProduct.active ? 'ativado' : 'inativado'} com sucesso!`,
        duration: 5,
      });
    } catch (error) {
      console.error('Erro ao alternar status do produto:', error);
      ToastMessage({
        type: 'error',
        content:
          'Ocorreu um erro ao alternar o status do produto. Por favor, tente novamente.',
      });
    } finally {
      setSwitchActiveProduct(null);
      setIsLoadingSwitchStatus(false);
    }
  };

  const handleCancelSwitchActiveProduct = () => {
    setSwitchActiveProduct(null);
  };

  const columns = useMemo<PageListProps<ProductDto>['columns']>(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        width: '100px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['id'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'publicName',
        title: 'Nome do Produto',
        dataIndex: 'publicName',
        minWidth: 250,
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['publicName'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'profileId',
        title: 'Profile',
        dataIndex: 'profileId',
        width: '250px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['profileId'],
        render: (profileId: ProductDto['profileId']) => {
          const profileName = profilesData?.find(
            profile => profile.id === profileId
          )?.name;
          return profileId !== null
            ? `${profileId}${profileName ? ` - ${profileName}` : ''}`
            : '-';
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'warrantyDays',
        title: 'Garantia',
        dataIndex: 'warrantyDays',
        width: '150px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['warrantyDays'],
        render: (warrantyDays: ProductDto['warrantyDays']) => {
          return !!warrantyDays
            ? `${warrantyDays} dia${warrantyDays > 1 ? 's' : ''}`
            : '-';
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'providerId',
        title: 'Adquirente',
        dataIndex: 'providerId',
        width: '170px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['providerId'],
        render: (providerId: ProductDto['providerId']) => {
          const paymentProvider = providersData?.find(
            provider => provider.id === providerId
          )?.name;
          return paymentProvider
            ? firstLetterUpperCase(paymentProvider)
            : `${providerId || '-'}`;
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'subscriptionType',
        title: 'Mensal',
        width: '150px',
        dataIndex: 'subscriptionType',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['subscriptionType'],
        render: (subscriptionType: ProductDto['subscriptionType']) => {
          return subscriptionType === 'monthly' ? 'Sim' : 'Não';
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'isDirectFallback',
        title: 'Recorrente',
        dataIndex: 'isDirectFallback',
        width: '170px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['isDirectFallback'],
        render: (isDirectFallback: ProductDto['isDirectFallback']) => {
          return isDirectFallback ? 'Sim' : 'Não';
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'isPrincipal',
        title: 'É principal?',
        dataIndex: 'isPrincipal',
        width: '170px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['isPrincipal'],
        render: (isPrincipal: ProductDto['isPrincipal']) => {
          return isPrincipal ? 'Sim' : 'Não';
        },
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
      },
      {
        key: 'active',
        title: 'Status',
        dataIndex: 'active',
        width: '150px',
        sorter: true,
        sortOrder: tableColumnSortOrderSymbol['active'],
        onHeaderCell: columns => {
          return {
            onClick: () => {
              handleChangeOrder(columns);
            },
          };
        },
        render: (status: ProductDto['active']) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Tag color={colorByStatus[status ? 'active' : 'inactive']}>
                {productStatuses[status ? 'active' : 'inactive']}
              </Tag>
            </div>
          );
        },
      },
      {
        title: 'Ações',
        key: 'action',
        dataIndex: '',
        width: '100px',
        align: 'center',
        render: (item: ProductDto) => {
          const menu: MenuProps = {
            items: [
              {
                key: 'viewOffers',
                label: 'Visualizar ofertas',
              },
              {
                key: 'viewProduct',
                label: 'Visualizar produto',
              },
              {
                key: 'createOffer',
                label: 'Criar oferta',
                disabled: !item.active,
              },
              {
                key: 'editProduct',
                label: 'Editar produto',
                disabled: !item.active,
              },
              {
                key: 'copyProductLink',
                label: 'Copiar Link Checkout',
              },
              {
                key: 'switchActiveProduct',
                label: `${item.active ? 'Inativar' : 'Ativar'} produto`,
              },
            ],
            onClick: ({ key }: { key: string }) => {
              const actionsKey: Record<string, () => void> = {
                viewOffers: () => navigate(`/products/${item.id}/offers`),
                editProduct: () => navigate(`/products/${item.id}/edit`),
                viewProduct: () =>
                  navigate(`/products/${item.id}/read?isViewingMode=true`),
                createOffer: () =>
                  navigate(`/products/${item.id}/offers/create`, {
                    state: { productId: item.id, productName: item.name },
                  }),
                copyProductLink: () => {
                  void navigator.clipboard.writeText(
                    buildProductCheckoutLink(item.id)
                  );
                },
                switchActiveProduct: () => {
                  setSwitchActiveProduct(item);
                },
              };

              if (key) {
                actionsKey[key]?.();
              }
            },
          };

          return (
            <Dropdown trigger={['click']} arrow={false} menu={menu}>
              <ButtonMenu type='text'>
                <MoreOutlined />
              </ButtonMenu>
            </Dropdown>
          );
        },
      },
    ],
    [
      handleChangeOrder,
      navigate,
      profilesData,
      providersData,
      tableColumnSortOrderSymbol,
    ]
  );

  const columnOrderState = usePageListColumnOrder(columns, {
    enabled: true,
    persist: true,
    storageKey: PRODUCT_DRAG_COLUMN_STORAGE_KEY,
    fixedColumnKeys: PRODUCT_FIXED_COLUMN_KEYS,
  });
  const columnVisibilityState = usePageListColumnVisibility(
    columnOrderState.orderedColumns,
    PRODUCT_COLUMN_VISIBILITY_CONFIG
  );

  const createButtonComponent = useMemo(() => {
    return (
      <Row style={{ gap: '12px' }}>
        <ActionsOfferBulk />
        <Button
          type='primary'
          onClick={() => {
            navigate(`/products/create`);
          }}
        >
          Criar produto
        </Button>
      </Row>
    );
  }, [navigate]);

  const hasResourcesLoading = useMemo(() => {
    return isLoadingProducts || isLoadingProfiles || isLoadingProviders;
  }, [isLoadingProducts, isLoadingProfiles, isLoadingProviders]);

  const optionsTabProducts = useMemo<
    TabsProductsProps['optionsTabProducts']
  >(() => {
    if (hasResourcesLoading) return [];

    return [
      {
        label: 'Produto Digital',
        component: (
          <ProductTable
            columns={columns}
            currentFilter={currentFilter}
            pageSize={currentFilter?.limit || PRODUCT_PAGE_SIZE}
            handleChangeFilter={handleChangeFilter}
            handleChangePagination={handleChangePagination}
            initialFilterData={currentFilter.filter}
            productsData={productsData || { count: 0, rows: [] }}
            columnOrderState={columnOrderState}
            columnVisibilityState={columnVisibilityState}
            profilesData={profilesData || []}
            providersData={providersData || []}
          />
        ),
      },
      {
        label: 'Produto Físico',
        component: <></>,
        disabled: true,
      },
    ];
  }, [
    columns,
    currentFilter,
    handleChangeFilter,
    handleChangePagination,
    productsData,
    profilesData,
    providersData,
    hasResourcesLoading,
    columnOrderState,
    columnVisibilityState,
  ]);

  return {
    columns,
    columnOrderState,
    columnVisibilityState,
    hasResourcesLoading,
    createButtonComponent,
    optionsTabProducts,
    providersData,
    switchActiveProduct,
    handleSwitchActiveProduct,
    handleCancelSwitchActiveProduct,
    isLoadingSwitchStatus,
    DrawerOfferBulk,
  };
}

const ButtonMenu = styled(Button)`
  &:focus {
    color: #3c89e8 !important;
  }
`;
